import _ from "lodash";
import React, { useState } from "react";
import moment from "moment";
import * as csv from "fast-csv";
import Button from "components/Shared/Button";
import TextInputField from "components/Shared/TextInputField";
import { fetchWorkflowsByAppointmentCreatedReport } from "services/report";
import { WORKFLOW_APPT_STATUS, WORKFLOW_APPT_STATUS_OPTIONS } from "../../../../constants";
import useReportData from "../useReportData";

// Column order is fixed by SE-750, so the CSV is built from this list rather
// than from the keys of the first row.
const COLUMNS = [
  ["appointmentCreatedDate", "Appointment Created Date"],
  ["appointmentDate", "Appointment Date"],
  ["urn", "URN"],
  ["category", "Category"],
  ["client", "Client"],
  ["DOB", "DOB"],
  ["practitioner", "Case Manager"],
  ["hospital", "Hospital"],
  ["specialty", "Specialty"],
  ["createdAt", "WF Creation Date"],
  ["newOrReview", "New or Review"],
  ["expiryDate", "Referrer Expiry Date"],
  ["dueWeeks", "Due Weeks"],
  ["status", "Status"],
  ["appointmentNotes", "Appointment Notes"],
];

// The API sends Cancelled / Rescheduled as labels already, and the workflow
// status value (e.g. "checkOut") for anything else.
const toStatusLabel = (status) =>
  _.get(
    _.find(WORKFLOW_APPT_STATUS_OPTIONS, { value: status }) || _.find(WORKFLOW_APPT_STATUS, { value: status }),
    "label",
    status
  );

const WorkflowsByAppointmentCreatedReport = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const validationError =
    startDate && endDate && startDate > endDate ? "The start date can not be date after end date!" : "";
  const isReady = Boolean(startDate) && Boolean(endDate) && !validationError;

  const { isLoading, failureReason, info, isEmpty, scheduleLoad, loadNow, download } = useReportData({
    cacheKey: `${startDate}~${endDate}`,
    isReady,
    fileName: `workflow_by_appointment_created_report_${startDate}-${endDate}.csv`,
    infoMessage: `Preparing workflows by appointment created date report(${startDate} - ${endDate})...`,
    emptyMessage: "No appointments created in given date range",
    loadCsv: async () => {
      let reportRawData;
      try {
        ({ data: reportRawData } = await fetchWorkflowsByAppointmentCreatedReport({ startDate, endDate }));
      } catch (error) {
        throw new Error(_.get(error, "response.data.error", error.message));
      }
      if (!reportRawData?.length) {
        return { rowCount: 0 };
      }
      const rows = [COLUMNS.map(([, label]) => label)].concat(
        reportRawData.map((row) =>
          COLUMNS.map(([key]) => {
            const value = key === "status" ? toStatusLabel(_.get(row, key, "")) : _.get(row, key, "");
            return _.isNil(value) ? "" : String(value);
          })
        )
      );
      return { csv: await csv.writeToString(rows), rowCount: reportRawData.length };
    },
  });

  return (
    <>
      <p className="menu-label">Workflows Report by Appointment Created Date</p>
      <div className="columns">
        <div className="column is-one-quarter">
          <TextInputField
            label="Start date"
            isRequired={true}
            hasError={false}
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              scheduleLoad();
            }}
            onBlur={loadNow}
            placeholder={moment().format("YYYY-MM-DD")}
            type="date"
          />
        </div>
        <div className="column is-one-quarter">
          <TextInputField
            label="End date"
            isRequired={true}
            hasError={false}
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              scheduleLoad();
            }}
            onBlur={loadNow}
            placeholder={moment().format("YYYY-MM-DD")}
            type="date"
          />
        </div>
      </div>
      <Button
        isFullWidth={false}
        disabled={!isReady || isLoading || isEmpty}
        isLoading={isLoading}
        onClick={download}
        text="Download"
      />
      <p className="has-text-danger is-size-7">{validationError || failureReason}</p>
      <p className="has-text-info is-size-7">{info}</p>
    </>
  );
};

export default WorkflowsByAppointmentCreatedReport;
